import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import { StatsIcon as DumbStatsIcon } from '@sorare/core/src/components/draft/StatsIcon';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { normalizeStatsFromRawScores } from '@sorare/core/src/lib/performance';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import type { Color } from '@sorare/core/src/style/types';

import { AnyPlayerFifteenStatsIcon } from '#src/components/player/AnyPlayerFifteenStatsIcon/index.tsx';
import { AnyPlayerStatsIcon } from '#src/components/player/AnyPlayerStatsIcon/index.tsx';
import {
  type TeamMode,
  useTeamMode,
} from '#src/searchCommonPlayer/teamMode.ts';

import type { AnyStatsIconCell_anyPlayer } from './__generated__/index.graphql.ts';

const Wrapper = styled.div`
  width: var(--player-stats-icon-width);
  flex-shrink: 0;
  display: none;
  &.showOnMobile {
    display: block;
  }
  @media ${laptopAndAbove} {
    display: block;
  }
`;

const GREY_BAR_COLOR: Color = 'var(--c-nd-300)';

const oldestFirstPaddedTo = (
  newestFirstTeamModes: (TeamMode | null)[],
  size: number
): (TeamMode | null)[] => {
  const kept = newestFirstTeamModes.slice(0, size);
  return [...new Array(size - kept.length).fill(null), ...kept.reverse()];
};

const statsGreyedOutsideTeamMode = ({
  anyPlayer,
  gameTeamModes,
  teamMode,
  size,
}: {
  anyPlayer: AnyStatsIconCell_anyPlayer;
  gameTeamModes: (TeamMode | null)[];
  teamMode: TeamMode;
  size: number;
}) => {
  const barsOldestFirst = normalizeStatsFromRawScores(
    (anyPlayer.rawFifteenPlayerGameScores ?? []).slice(0, size),
    anyPlayer.sport,
    size
  );
  const teamModesOldestFirst = oldestFirstPaddedTo(gameTeamModes, size);

  return barsOldestFirst.map((bar, i) => {
    const barTeamMode = teamModesOldestFirst[i];
    return barTeamMode && barTeamMode !== teamMode
      ? { ...bar, color: GREY_BAR_COLOR }
      : bar;
  });
};

type Props = {
  anyPlayer: AnyStatsIconCell_anyPlayer;
  onStatsClick: () => void;
  showOnMobile?: boolean;
  /**
   * 15 most-recent per-game team modes, newest first — same order and length as
   * `rawFifteenPlayerGameScores`. When set, bars played in the other team mode
   * than the active `?tm=` one are greyed out.
   */
  recentGameTeamModes?: (TeamMode | null)[];
};

export const AnyStatsIconCell = ({
  anyPlayer,
  onStatsClick,
  showOnMobile,
  recentGameTeamModes,
}: Props) => {
  const isLaptop = useIsLaptop();
  const teamMode = useTeamMode();

  const teamModeAwareStats =
    recentGameTeamModes && teamMode !== 'ALL'
      ? statsGreyedOutsideTeamMode({
          anyPlayer,
          gameTeamModes: recentGameTeamModes,
          teamMode,
          size: isLaptop ? 15 : 5,
        })
      : null;

  return (
    <Wrapper className={classNames({ showOnMobile })}>
      {teamModeAwareStats ? (
        <DumbStatsIcon
          stats={teamModeAwareStats}
          onClick={onStatsClick}
          width={isLaptop ? 10 : 4}
          height={isLaptop ? 4 : 3}
        />
      ) : isLaptop ? (
        <AnyPlayerFifteenStatsIcon player={anyPlayer} onClick={onStatsClick} />
      ) : (
        <AnyPlayerStatsIcon player={anyPlayer} onClick={onStatsClick} />
      )}
    </Wrapper>
  );
};

AnyStatsIconCell.fragments = {
  anyPlayer: gql`
    fragment AnyStatsIconCell_anyPlayer on AnyPlayerInterface {
      slug
      sport
      ...AnyPlayerStatsIcon_anyPlayer
      ...AnyPlayerFifteenStatsIcon_anyPlayer
    }
    ${AnyPlayerStatsIcon.fragments.anyPlayer}
    ${AnyPlayerFifteenStatsIcon.fragments.anyPlayer}
  ` as TypedDocumentNode<AnyStatsIconCell_anyPlayer>,
};
